import { Component, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { BehaviorSubject, Observable, catchError, combineLatest, firstValueFrom, map, of, shareReplay, switchMap, startWith, tap } from 'rxjs';

import { WorldsService, WorldGetResponse, WorldSummary } from '../../api/worlds.service';
import {
	WorldModuleCredentialsService,
	ModuleCredentialsResponse,
	RotateSecretResponse,
} from '../../api/world-module-credentials.service';
import { WorldNavCountsService, WorldNavCountsResponse } from '../../api/world-nav-counts.service';
import { EntityShellComponent, BasicField, TitleAction, PrimaryAction, Breadcrumb } from '../../shared/components/entity-shell.component';
import { EntityTabsComponent, EntityTab } from '../../shared/components/entity-tabs.component';
import { EntityImageFieldComponent } from '../../shared/components/entity-image-field.component';
import { EditorModule } from '@tinymce/tinymce-angular';

type WorldOverviewVm = {
	world: WorldSummary;
	options: WorldGetResponse['options'];
	counts: WorldNavCountsResponse | null;
	loading: boolean;
	error: string | null;
};

type CredentialsVm = ModuleCredentialsResponse | null;

@Component({
	selector: 'of-world-overview-page',
	standalone: true,
	imports: [CommonModule, ReactiveFormsModule, RouterModule, EntityShellComponent, EntityTabsComponent, EntityImageFieldComponent, EditorModule],
	styleUrls: ['./world-overview.page.scss'],
	template: `
		<ng-container *ngIf="vm$ | async as vm">
			<section *ngIf="vm.error" class="world-detail-placeholder">
				<p class="support-text">{{ vm.error }}</p>
				<button type="button" class="btn-outline" (click)="refresh()">Try again</button>
			</section>

			<section *ngIf="vm.loading && !vm.error" class="world-detail-placeholder">
				<p class="support-text">Loading world data...</p>
			</section>

			<section *ngIf="!vm.loading && !vm.error">
				<ng-template #tabRegions>
					<div class="entity-list">
						<p class="support-text">Regions</p>
						<button type="button" class="btn-outline" (click)="goTo('regions/new')">Add Region</button>
						<button type="button" class="btn-outline slim" (click)="goTo('regions')">Open Regions</button>
					</div>
				</ng-template>
				<ng-template #tabPois>
					<div class="entity-list">
						<p class="support-text">Points of Interest</p>
						<button type="button" class="btn-outline" (click)="goTo('points-of-interest/new')">Add POI</button>
						<button type="button" class="btn-outline slim" (click)="goTo('points-of-interest')">Open POIs</button>
					</div>
				</ng-template>
				<ng-template #tabMedia>
					<div class="entity-list">
						<p class="support-text">Manage world media.</p>
						<button type="button" class="btn-outline" (click)="goTo('media')">Open Media Library</button>
					</div>
				</ng-template>
				<ng-template #tabNpcs>
					<div class="entity-list">
						<button type="button" class="btn-outline" (click)="goTo('non-player-characters/new')">Add NPC</button>
						<button type="button" class="btn-outline slim" (click)="goTo('non-player-characters')">Open NPCs</button>
					</div>
				</ng-template>
				<ng-template #tabPcs>
					<div class="entity-list">
						<button type="button" class="btn-outline" (click)="goTo('player-characters/new')">Add PC</button>
						<button type="button" class="btn-outline slim" (click)="goTo('player-characters')">Open PCs</button>
					</div>
				</ng-template>
				<ng-template #tabFactions>
					<div class="entity-list">
						<button type="button" class="btn-outline" (click)="goTo('factions/new')">Add Faction</button>
						<button type="button" class="btn-outline slim" (click)="goTo('factions')">Open Factions</button>
					</div>
				</ng-template>
				<ng-template #tabMerchants>
					<div class="entity-list">
						<button type="button" class="btn-outline" (click)="goTo('merchants/new')">Add Merchant</button>
						<button type="button" class="btn-outline slim" (click)="goTo('merchants')">Open Merchants</button>
					</div>
				</ng-template>
				<ng-template #tabItems>
					<div class="entity-list">
						<button type="button" class="btn-outline" (click)="goTo('items/new')">Add Item</button>
						<button type="button" class="btn-outline slim" (click)="goTo('items')">Open Items</button>
					</div>
				</ng-template>
				<ng-template #tabGoods>
					<div class="entity-list">
						<button type="button" class="btn-outline" (click)="goTo('goods-services/new')">Add Goods/Service</button>
						<button type="button" class="btn-outline slim" (click)="goTo('goods-services')">Open Goods/Services</button>
					</div>
				</ng-template>
				<ng-template #tabTomes>
					<div class="entity-list">
						<button type="button" class="btn-outline" (click)="goTo('tomes/new')">Add Tome</button>
						<button type="button" class="btn-outline slim" (click)="goTo('tomes')">Open Tomes</button>
					</div>
				</ng-template>
				<ng-template #tabQuests>
					<div class="entity-list">
						<button type="button" class="btn-outline" (click)="goTo('quests/new')">Add Quest</button>
						<button type="button" class="btn-outline slim" (click)="goTo('quests')">Open Quests</button>
					</div>
				</ng-template>
				<ng-template #tabEncounters>
					<div class="entity-list">
						<button type="button" class="btn-outline" (click)="goTo('encounters/new')">Add Encounter</button>
						<button type="button" class="btn-outline slim" (click)="goTo('encounters')">Open Encounters</button>
					</div>
				</ng-template>
				<ng-template #tabPantheons>
					<div class="entity-list">
						<button type="button" class="btn-outline" (click)="goTo('pantheons/new')">Add Pantheon</button>
						<button type="button" class="btn-outline slim" (click)="goTo('pantheons')">Open Pantheons</button>
					</div>
				</ng-template>
				<ng-template #tabGods>
					<div class="entity-list">
						<button type="button" class="btn-outline" (click)="goTo('gods/new')">Add God</button>
						<button type="button" class="btn-outline slim" (click)="goTo('gods')">Open Gods</button>
					</div>
				</ng-template>
				<ng-template #tabDomains>
					<div class="entity-list">
						<button type="button" class="btn-outline" (click)="goTo('domains/new')">Add Domain</button>
						<button type="button" class="btn-outline slim" (click)="goTo('domains')">Open Domains</button>
					</div>
				</ng-template>

				<of-entity-shell
					[basicFields]="[]"
					[breadcrumbs]="getWorldBreadcrumbs(vm.world.name)"
					[title]="vm.world.name"
					[foundryId]="vm.world.foundryId ?? null"
					[editable]="true"
					[onEdit]="editHandler"
					[titleActions]="titleActions"
					[primaryActions]="primaryActions"
					[hasImage]="!!basicForm.value.imageAssetId"
					[hasCustomBasic]="true"
				>
					<div basic class="world-basic" [formGroup]="basicForm">
						<div class="basic-row">
							<div class="world-basic__image">
								<of-entity-image-field
									formControlName="imageAssetId"
									[worldId]="worldId"
									[label]="'World image'"
									[modalKicker]="'World'"
									[assetType]="'world-image'"
									[assetPurpose]="'world-image'"
									[blobPathPrefix]="'worlds/' + worldId + '/images'"
									(uploadingChange)="uploading = $event"
									(errorChange)="uploadError = $event"
								></of-entity-image-field>
							</div>
							<div class="world-basic__content" [formGroup]="basicForm">
								<label class="forge-field wide rich-field">
									<span>Description</span>
									<editor
										formControlName="description"
										[init]="tinymceConfig"
										[id]="'world-desc-editor'"
									></editor>
								</label>
							</div>
						</div>
						<div class="extra-fields">
							<div class="inline-fields">
								<label class="forge-field">
									<span>System</span>
									<input type="text" [value]="vm.world.systemType" readonly />
								</label>
							</div>
						</div>
						<div class="basic-footer">
							<div class="form-alert error" *ngIf="uploadError">{{ uploadError }}</div>
							<button type="button" class="btn-primary-gradient" (click)="saveBasic()" [disabled]="savingBasic || uploading">
								{{ savingBasic ? 'Saving...' : 'Save' }}
							</button>
						</div>
					</div>

					<div tabs>
						<of-entity-tabs
							[tabs]="tabsWithCounts(vm.counts, {
								media: tabMedia,
								regions: tabRegions,
								pois: tabPois,
								npcs: tabNpcs,
								pcs: tabPcs,
								factions: tabFactions,
								merchants: tabMerchants,
								items: tabItems,
								goods: tabGoods,
								tomes: tabTomes,
								quests: tabQuests,
								encounters: tabEncounters,
								pantheons: tabPantheons,
								gods: tabGods,
								domains: tabDomains
							})"
							[(activeTab)]="activeTab"
						></of-entity-tabs>
					</div>
				</of-entity-shell>
			</section>

			<div *ngIf="showEdit" class="modal-overlay" (click)="closeEdit()">
				<form class="modal-card" [formGroup]="form" (click)="$event.stopPropagation()">
					<div class="modal-header">
						<div>
							<p class="kicker">World</p>
							<h3>Edit metadata</h3>
						</div>
						<button type="button" class="btn-icon" (click)="closeEdit()" aria-label="Close edit metadata dialog">&times;</button>
					</div>

					<div class="modal-body">
						<div class="modal-grid">
							<label class="forge-field">
								<span>Name</span>
								<input type="text" formControlName="name" />
							</label>
							<label class="forge-field">
								<span>System / ruleset</span>
								<select formControlName="systemRulesetId">
									<option *ngFor="let option of currentOptions?.systems ?? []" [value]="option.id">
										{{ option.name }}
									</option>
								</select>
							</label>
							<label class="forge-field">
								<span>Era</span>
								<select formControlName="eraId">
									<option *ngFor="let option of currentOptions?.eras ?? []" [value]="option.id">
										{{ option.name }}
									</option>
								</select>
							</label>
							<label class="forge-field">
								<span>Tech level</span>
								<select formControlName="techLevelId">
									<option *ngFor="let option of currentOptions?.techLevels ?? []" [value]="option.id">
										{{ option.name }}
									</option>
								</select>
							</label>
							<label class="forge-field">
								<span>Status</span>
								<select formControlName="status">
									<option *ngFor="let status of statusOptions" [value]="status.toLowerCase()">{{ status }}</option>
								</select>
							</label>
							<label class="forge-field">
								<span>Dominant culture</span>
								<select formControlName="dominantCultureId">
									<option [value]="''">(none)</option>
									<option *ngFor="let culture of cultureOptions" [value]="culture.id">
										{{ culture.name }}
									</option>
								</select>
							</label>
						</div>

						<div class="modal-actions">
							<button type="button" class="btn-outline" (click)="archive()" [disabled]="submitting">Archive World</button>
							<div class="button-row" style="margin-left: auto;">
								<button type="button" class="btn-outline" (click)="closeEdit()">Cancel</button>
								<button
									type="button"
									class="btn-primary-gradient"
									[disabled]="form.invalid || submitting"
									(click)="save()"
								>
									{{ submitting ? 'Saving...' : 'Save changes' }}
								</button>
							</div>
						</div>
					</div>
				</form>
			</div>

			<div *ngIf="showCredentials" class="modal-overlay" (click)="closeCredentials()">
				<div class="modal-card" (click)="$event.stopPropagation()">
					<div class="modal-header">
						<div>
							<p class="kicker">World</p>
							<h3>Credential Manager</h3>
						</div>
						<button type="button" class="btn-icon" (click)="closeCredentials()" aria-label="Close credential manager dialog">
							&times;
						</button>
					</div>

					<div class="modal-body">
						<ng-container *ngIf="credentials$ | async as creds; else credsLoading">
							<div *ngIf="credsError" class="form-alert error">{{ credsError }}</div>
							<div class="forge-form">
								<label class="forge-field">
									<span>World ID</span>
									<div class="button-row" style="gap: 8px;">
										<input type="text" [value]="worldId" readonly />
										<button type="button" class="btn-outline slim" (click)="copy(worldId)">Copy</button>
									</div>
								</label>
								<label class="forge-field">
									<span>API Key</span>
									<div class="button-row" style="gap: 8px;">
										<input type="text" [value]="creds?.apiKey ?? ''" readonly />
										<button type="button" class="btn-outline slim" (click)="copy(creds?.apiKey ?? '')" [disabled]="!creds?.apiKey">
											Copy
										</button>
									</div>
								</label>
								<label class="forge-field">
									<span>API Secret</span>
									<div class="button-row" style="gap: 8px;">
										<input
											type="text"
											[value]="rotatedSecret ?? ''"
											placeholder="{{ creds?.hasSecret ? 'Stored (hidden)' : 'Not generated yet' }}"
											readonly
										/>
										<button type="button" class="btn-outline slim" (click)="copy(rotatedSecret ?? '')" [disabled]="!rotatedSecret">
											Copy
										</button>
									</div>
								</label>
								<div class="support-text">
									Secret created: {{ creds?.secretCreatedAt ? (creds.secretCreatedAt | date: 'medium') : 'Not generated' }}
								</div>
								<div class="modal-actions">
									<div class="button-row">
										<button type="button" class="btn-primary-gradient" (click)="rotateSecret()" [disabled]="credBusy">
											{{ credBusy ? 'Working...' : creds?.hasSecret ? 'Rotate secret' : 'Generate secret' }}
										</button>
										<button type="button" class="btn-outline" (click)="refreshCredentials()" [disabled]="credBusy">
											Refresh
										</button>
									</div>
									<button type="button" class="btn-outline" (click)="closeCredentials()">Close</button>
								</div>
								<div class="support-text">The secret is only shown immediately after generating or rotating it.</div>
							</div>
						</ng-container>
						<ng-template #credsLoading>
							<p class="support-text">Loading credentials...</p>
						</ng-template>
					</div>
				</div>
			</div>
		</ng-container>
	`,
})
export class WorldOverviewPage {
	vm$: Observable<WorldOverviewVm>;
	credentials$: Observable<CredentialsVm>;

	worldId = '';
	activeTab = 'media';
	statusOptions = ['Draft', 'Linked', 'Archived'];
	cultureOptions: Array<{ id: string; name: string }> = [];

	submitting = false;
	showEdit = false;
	showCredentials = false;
	currentOptions: WorldGetResponse['options'] | null = null;
	latestVm: WorldOverviewVm | null = null;
	titleActions: TitleAction[] = [];
	primaryActions: PrimaryAction[] = [];
	editHandler = () => {
		if (this.latestVm) {
			this.openEdit(this.latestVm);
		}
	};

	credBusy = false;
	credsError: string | null = null;
	rotatedSecret: string | null = null;
	savingBasic = false;
	uploading = false;
	uploadError: string | null = null;

	private readonly refresh$ = new BehaviorSubject<void>(undefined);
	private readonly credsRefresh$ = new BehaviorSubject<void>(undefined);
	tinymceConfig = {
		base_url: '/tinymce',
		suffix: '.min',
		license_key: 'gpl',
		menubar: false,
		statusbar: false,
		branding: false,
		promotion: false,
		skin: 'oxide-dark',
		content_css: false,
		plugins: 'advlist lists link table code quickbars autolink',
		toolbar:
			'styleselect | bold italic underline strikethrough | forecolor backcolor | bullist numlist | alignleft aligncenter alignright | outdent indent | table link | removeformat code',
		quickbars_insert_toolbar: 'bullist numlist hr table',
		quickbars_selection_toolbar: 'bold italic underline | blockquote | link | removeformat',
		style_formats: [
			{ title: 'Paragraph', format: 'p' },
			{ title: 'Heading 3', format: 'h3' },
			{ title: 'Heading 4', format: 'h4' },
			{ title: 'Heading 5', format: 'h5' },
			{ title: 'Blockquote', format: 'blockquote' },
		],
		content_style: 'body { font-family: var(--font-body, Inter), sans-serif; color: #fff; background: #1f1f25; }',
		height: 260,
	};

	form = this.fb.group({
		name: ['', [Validators.required, Validators.maxLength(200)]],
		systemRulesetId: ['', [Validators.required]],
		eraId: ['', [Validators.required]],
		techLevelId: ['', [Validators.required]],
		status: ['draft', [Validators.required]],
		dominantCultureId: [''],
	});

	basicForm = this.fb.group({
		description: [''],
		imageAssetId: [''],
	});

	tabs: Array<{ id: string; label: string; icon: string; countKey: keyof WorldNavCountsResponse | null; accent: string }> = [
		{ id: 'media', label: 'Media', icon: '🖼️', countKey: null, accent: '#6ba6ff' },
		{ id: 'regions', label: 'Regions', icon: '🌐', countKey: 'regions', accent: '#2ec4ff' },
		{ id: 'pois', label: 'Points of Interest', icon: '🗺️', countKey: 'pointsOfInterest', accent: '#2ec4ff' },
		{ id: 'npcs', label: 'NPCs', icon: '👤', countKey: 'nonPlayerCharacters', accent: '#3ddc97' },
		{ id: 'pcs', label: 'PCs', icon: '👥', countKey: 'playerCharacters', accent: '#3ddc97' },
		{ id: 'factions', label: 'Factions', icon: '🛡️', countKey: 'factions', accent: '#3ddc97' },
		{ id: 'merchants', label: 'Merchants', icon: '🏪', countKey: 'merchants', accent: '#3ddc97' },
		{ id: 'items', label: 'Items', icon: '📦', countKey: 'items', accent: '#f2b25f' },
		{ id: 'goods', label: 'Goods/Services', icon: '👜', countKey: 'goodsServices', accent: '#f2b25f' },
		{ id: 'tomes', label: 'Tomes', icon: '📘', countKey: 'tomes', accent: '#f2b25f' },
		{ id: 'quests', label: 'Quests', icon: '🏳️', countKey: 'quests', accent: '#ff6b6b' },
		{ id: 'encounters', label: 'Encounters', icon: '🎯', countKey: 'encounters', accent: '#ff6b6b' },
		{ id: 'pantheons', label: 'Pantheons', icon: '⚛️', countKey: 'pantheons', accent: '#a97bff' },
		{ id: 'gods', label: 'Gods', icon: '✨', countKey: 'gods', accent: '#a97bff' },
		{ id: 'domains', label: 'Domains', icon: '🖼️', countKey: 'domains', accent: '#a97bff' },
	];

	constructor(
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		private readonly worlds: WorldsService,
		private readonly fb: FormBuilder,
		private readonly moduleCredentials: WorldModuleCredentialsService,
		private readonly counts: WorldNavCountsService
	) {
		this.worldId = this.route.parent!.snapshot.paramMap.get('worldId') ?? '';

		const world$ = this.refresh$.pipe(
			switchMap(() =>
				this.worlds.get(this.worldId).pipe(
					map((res) => ({
						loading: false,
						error: null as string | null,
						world: res.world,
						options: res.options,
					})),
					catchError((err) =>
						of({
							loading: false,
							error: err instanceof Error ? err.message : 'Unable to load world',
							world: null as any,
							options: null as any,
						})
					),
					startWith({
						loading: true,
						error: null as string | null,
						world: null as any,
						options: null as any,
					})
				)
			)
		);

		const counts$ = this.counts
			.get(this.worldId)
			.pipe(catchError(() => of(null)), shareReplay({ bufferSize: 1, refCount: true }));

		this.vm$ = combineLatest([world$, counts$]).pipe(
			map(([world, counts]) => {
				const summary = world.world ?? ({} as WorldSummary);
				const options = world.options ?? this.currentOptions ?? { systems: [], eras: [], techLevels: [], cultures: [] };
				this.currentOptions = options;
				this.cultureOptions = options.cultures ?? [];
				return {
					world: summary,
					options,
					counts,
					loading: world.loading,
					error: world.error,
				};
			}),
			tap((vm: WorldOverviewVm) => {
				this.latestVm = vm;
				this.titleActions = [
					{ label: 'Edit Metadata', handler: () => this.openEdit(vm) },
					{ label: 'Credential Manager', handler: () => this.openCredentials() },
				];
				this.primaryActions = [];
				if (vm.world) {
					const existingAsset = vm.world.mapAssetId ?? '';
					this.basicForm.patchValue(
						{
							description: vm.world.mapAttribution ?? '',
							imageAssetId: existingAsset,
						},
						{ emitEvent: false }
					);
				}
			})
		);

		this.credentials$ = this.credsRefresh$.pipe(
			switchMap(() =>
				this.moduleCredentials.get(this.worldId).pipe(
					catchError(() => {
						this.credsError = 'Unable to load credentials';
						return of(null);
					})
				)
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	refresh() {
		this.refresh$.next();
	}

	selectTab(tabId: string) {
		this.activeTab = tabId;
	}

	basicFields(world: WorldSummary): BasicField[] {
		return [
			{ label: 'System', value: world.systemType },
			{ label: 'Era', value: world.era },
			{ label: 'Tech Level', value: world.techLevel },
			{ label: 'Status', value: world.status },
			{ label: 'Description', value: world.mapAttribution, multiline: true, colSpan: 2 },
			{ label: 'Dominant Culture', value: world.dominantCulture },
		];
	}

	tabsWithCounts(counts: WorldNavCountsResponse | null, templates: Record<string, TemplateRef<any> | undefined>): EntityTab[] {
		return this.tabs.map((t) => ({
			id: t.id,
			label: t.label,
			icon: t.icon,
			accent: t.accent,
			count: t.countKey ? this.tabCount(counts, t.countKey) : null,
			content: templates[t.id],
		}));
	}

	tabCount(counts: WorldNavCountsResponse | null, key: keyof WorldNavCountsResponse | null) {
		if (!counts || !key) return 0;
		return counts[key] ?? 0;
	}

	openEdit(vm: WorldOverviewVm) {
		this.showEdit = true;
		if (vm.world && vm.options) {
			this.patchForm(vm.world, vm.options);
		}
	}

	getWorldBreadcrumbs(worldName: string): Breadcrumb[] {
		return [
			{ label: 'Worlds', route: ['/workspace', 'worlds'] },
			{ label: worldName },
		];
	}

	closeEdit() {
		this.showEdit = false;
		this.rotatedSecret = null;
		this.credsError = null;
	}

	save() {
		if (this.form.invalid || this.submitting) return;
		this.submitting = true;
		const value = this.form.getRawValue();
		this.worlds
			.update(this.worldId, {
				name: value.name!,
				systemRulesetId: value.systemRulesetId!,
				eraId: value.eraId!,
				techLevelId: value.techLevelId!,
				status: value.status ? value.status : null,
				version: null,
				dominantCultureId: value.dominantCultureId ?? null,
				dominantCulture: null,
			})
			.subscribe({
				next: () => {
					this.submitting = false;
					this.showEdit = false;
					this.refresh();
				},
				error: () => {
					this.submitting = false;
				},
			});
	}

	async saveBasic() {
		if (this.savingBasic || !this.latestVm?.world) return;
		const world = this.latestVm.world;
		const basic = this.basicForm.getRawValue();
		this.savingBasic = true;
		this.uploadError = null;

		try {
			const mapAssetId = basic.imageAssetId?.trim() || world.mapAssetId || null;

			await firstValueFrom(
				this.worlds.update(this.worldId, {
					name: world.name,
					systemRulesetId: world.systemRulesetId,
					eraId: world.eraId,
					techLevelId: world.techLevelId,
					status: world.status ?? 'draft',
					version: null,
					dominantCultureId: world.dominantCultureId ?? null,
					dominantCulture: null,
					mapAttribution: basic.description ?? null,
					mapAssetId,
				})
			);

			this.savingBasic = false;
			this.refresh();
		} catch (error) {
			this.savingBasic = false;
			this.uploadError = error instanceof Error ? error.message : 'Unable to save world details';
		}
	}

	// Image upload + preview are handled by of-entity-image-field.

	archive() {
		if (this.submitting) return;
		this.submitting = true;
		const value = this.form.getRawValue();
		this.worlds
			.update(this.worldId, {
				name: value.name!,
				systemRulesetId: value.systemRulesetId!,
				eraId: value.eraId!,
				techLevelId: value.techLevelId!,
				status: 'archived',
				version: null,
				dominantCultureId: value.dominantCultureId ?? null,
				dominantCulture: null,
			})
			.subscribe({
				next: () => {
					this.submitting = false;
					void this.router.navigate(['/workspace']);
				},
				error: () => {
					this.submitting = false;
				},
			});
	}

	goTo(path: string) {
		void this.router.navigate(['/workspace/worlds', this.worldId, path]);
	}

	openCredentials() {
		this.showCredentials = true;
		this.refreshCredentials();
	}

	closeCredentials() {
		this.showCredentials = false;
		this.rotatedSecret = null;
		this.credsError = null;
	}

	refreshCredentials() {
		this.credsError = null;
		this.credsRefresh$.next();
	}

	rotateSecret() {
		if (this.credBusy) return;
		this.credBusy = true;
		this.moduleCredentials.rotateSecret(this.worldId).subscribe({
			next: (res: RotateSecretResponse) => {
				this.rotatedSecret = res.apiSecret;
				this.credBusy = false;
				this.refreshCredentials();
			},
			error: (err) => {
				this.credBusy = false;
				this.credsError = err instanceof Error ? err.message : 'Unable to rotate secret';
			},
		});
	}

	copy(value: string) {
		if (!value) return;
		if (navigator?.clipboard?.writeText) {
			void navigator.clipboard.writeText(value);
		}
	}

	private patchForm(world: WorldSummary, options: WorldGetResponse['options']) {
		const cultureId =
			world.dominantCultureId ??
			options.cultures?.find((c) => c.name === world.dominantCulture)?.id ??
			'';

		this.form.patchValue({
			name: world.name ?? '',
			systemRulesetId: world.systemRulesetId ?? options.systems[0]?.id ?? '',
			eraId: world.eraId ?? options.eras[0]?.id ?? '',
			techLevelId: world.techLevelId ?? options.techLevels[0]?.id ?? '',
			status: (world.status ?? 'draft').toLowerCase(),
			dominantCultureId: cultureId,
		});
	}
}
